import React, { useState , useEffect, useMemo, useCallback } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    Select,
    MenuItem,
    IconButton,
    FormControl,
} from '@mui/material';
import { MdChevronLeft, MdChevronRight, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import { useTheme } from '@mui/material/styles';

/**
 * Reusable Table Component with Sorting
 * 
 * @param {Array} columns - Array of column definitions
 *   Example: [
 *     { id: 'name', label: 'Name', width: '20%', sortable: true },
 *     { id: 'age', label: 'Age', width: '15%', sortable: true },
 *     { id: 'actions', label: 'Actions', width: '15%', sortable: false },
 *   ]
 * 
 * @param {Array} data - Array of row data objects
 *   Example: [
 *     { name: 'Ali', age: '30', salary: '2000', department: 'CS', actions: <Button>Edit</Button> }
 *   ]
 * 
 * @param {Boolean} pagination - Enable/disable pagination (default: true)
 * @param {Number} defaultRowsPerPage - Default rows per page (default: 25)
 * @param {Array} rowsPerPageOptions - Options for rows per page (default: [10, 25, 50, 100])
 * @param {Function} onSort - Optional custom sort function (columnId, direction) => void
 * @param {Object} sortConfig - Optional external sort configuration { key: string, direction: 'asc' | 'desc' }
 */

const ReusableTable = ({
    columns = [],
    data = [],
    pagination = true,
    defaultRowsPerPage = 25,
    rowsPerPageOptions = [10, 25, 50, 100],
    onSort = null, // Optional: external sort handler
    sortConfig: externalSortConfig = null, // Optional: controlled sort state
    onRowClick = null, // Optional: called with (row) when a row is clicked
}) => {
    const theme = useTheme();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    
    // Internal sort state (used when not controlled externally)
    const [internalSortConfig, setInternalSortConfig] = useState({
        key: null,
        direction: 'asc',
    });

    // Use external sort config if provided, otherwise use internal
    const sortConfig = externalSortConfig || internalSortConfig;

    // Handle sort click
    const handleSort = (columnId) => {
        // Check if column is sortable
        const column = columns.find(col => col.id === columnId);
        if (!column || column.sortable === false) return;

        let direction = 'asc';
        
        // Toggle direction if clicking the same column
        if (sortConfig.key === columnId && sortConfig.direction === 'asc') {
            direction = 'desc';
        }

        const newSortConfig = { key: columnId, direction };

        // If external sort handler provided, use it
        if (onSort) {
            onSort(columnId, direction);
        } else {
            // Otherwise, update internal state
            setInternalSortConfig(newSortConfig);
        }
        
        // Reset to first page when sorting
        setPage(0);
    };

    // Sort data
    const getSortedData = () => {
        if (!sortConfig.key || !data || data.length === 0) return data;

        return [...data].sort((a, b) => {
            // 👇 Use raw sort value if available, fall back to the rendered cell
            const aValue = a[`_sort_${sortConfig.key}`] ?? a[sortConfig.key];
            const bValue = b[`_sort_${sortConfig.key}`] ?? b[sortConfig.key];

            let aCompare = React.isValidElement(aValue) ? extractTextFromElement(aValue) : aValue;
            let bCompare = React.isValidElement(bValue) ? extractTextFromElement(bValue) : bValue;

            if (aCompare == null && bCompare == null) return 0;
            if (aCompare == null) return 1;
            if (bCompare == null) return -1;

            if (typeof aCompare === 'number' && typeof bCompare === 'number') {
                return sortConfig.direction === 'asc' ? aCompare - bCompare : bCompare - aCompare;
            }

            return sortConfig.direction === 'asc'
                ? String(aCompare).localeCompare(String(bCompare))
                : String(bCompare).localeCompare(String(aCompare));
        });
    };

    // Extract text from React element for sorting
    const extractTextFromElement = (element) => {
        if (!element) return '';
        if (typeof element === 'string' || typeof element === 'number') return element;
        
        // Try to find text in props
        if (element.props) {
            if (element.props.children) {
                if (typeof element.props.children === 'string' || typeof element.props.children === 'number') {
                    return element.props.children;
                }
                if (Array.isArray(element.props.children)) {
                    return element.props.children
                        .map(child => extractTextFromElement(child))
                        .join('');
                }
            }
            // Check for common text props
            if (element.props.label) return element.props.label;
            if (element.props.value) return element.props.value;
        }
        
        return '';
    };

    // ✅ Sort once per (data, sortConfig) change, not on every render.
    // Without this, parents that rebuild `data` on every render were forcing an
    // O(n log n) sort + recursive React-element text extraction every keystroke
    // — a measurable cause of jank/freeze on large lists.
    const sortedData = useMemo(
        () => getSortedData(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data, sortConfig.key, sortConfig.direction]
    );
    const totalRecords = sortedData.length;
    const totalPages = Math.ceil(totalRecords / rowsPerPage);
    const startRecord = page * rowsPerPage + 1;
    const endRecord = Math.min((page + 1) * rowsPerPage, totalRecords);

    // ✅ Slice once per (sortedData, page, rowsPerPage) change.
    const currentPageData = useMemo(
        () => (pagination
            ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : sortedData),
        [sortedData, page, rowsPerPage, pagination]
    );

    // Handle page change
    const handleChangePage = useCallback((newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage);
        }
    }, [totalPages]);

    // Handle rows per page change
    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    // Clamp the current page when the dataset shrinks. Depend on data.length —
    // not the array reference — so consumers that rebuild `data` on every
    // render don't fire this effect every render.
    useEffect(() => {
        const newTotalPages = Math.ceil(data.length / rowsPerPage);
        setPage(prev => (prev >= newTotalPages ? Math.max(0, newTotalPages - 1) : prev));
    }, [data.length, rowsPerPage]);


    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            <TableContainer
                component={Paper}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    overflowX: 'auto',
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => {
                                const isSortable = column.sortable !== false;
                                const isActiveSortColumn = sortConfig.key === column.id;

                                return (
                                    <TableCell
                                        key={column.id}
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 600,
                                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                            width: column.width || 'auto',
                                            cursor: isSortable ? 'pointer' : 'default',
                                            userSelect: 'none',
                                            '&:hover': isSortable ? {
                                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                            } : {},
                                        }}
                                        onClick={() => isSortable && handleSort(column.id)}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <span>{column.label}</span>
                                            {isSortable && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        ml: 0.5,
                                                    }}
                                                >
                                                    {isActiveSortColumn && sortConfig.direction === 'asc' ? (
                                                        <MdArrowUpward
                                                            size={16}
                                                            color={theme.palette.primary.main}
                                                        />
                                                    ) : isActiveSortColumn && sortConfig.direction === 'desc' ? (
                                                        <MdArrowDownward
                                                            size={16}
                                                            color={theme.palette.primary.main}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                opacity: 0.3,
                                                            }}
                                                        >
                                                            <MdArrowUpward
                                                                size={12}
                                                                color={theme.palette.text.secondary}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currentPageData.length > 0 ? (
                            currentPageData.map((row, rowIndex) => (
                                <TableRow
                                    key={rowIndex}
                                    hover
                                    onClick={() => onRowClick && onRowClick(row)}
                                    sx={{
                                        cursor: onRowClick ? 'pointer' : 'default',
                                        '&:hover': {
                                            backgroundColor: 'rgba(245, 166, 35, 0.05)',
                                        },
                                    }}
                                >
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                            }}
                                        >
                                            {row[column.id]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    sx={{
                                        textAlign: 'center',
                                        color: theme.palette.text.secondary,
                                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                        py: 4,
                                    }}
                                >
                                    No data available
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination — render the strip even when totalRecords === 0 so the
                admin still sees an explicit "0 records found" confirmation. */}
            {pagination && (
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        mt: 2,
                        px: 2,
                    }}
                >
                    {/* Left side - Rows per page */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography
                            variant="body2"
                            sx={{ color: theme.palette.text.secondary }}
                        >
                            Showing
                        </Typography>
                        <FormControl size="small">
                            <Select
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                sx={{
                                    color: theme.palette.text.primary,
                                    backgroundColor: theme.palette.background.paper,
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                            >
                                {rowsPerPageOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Center - Records info */}
                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}
                    >
                        {totalRecords === 0
                            ? '0 records found'
                            : `${startRecord} : ${endRecord} of ${totalRecords} records`}
                    </Typography>

                    {/* Right side - Page navigation (hidden when there are no records) */}
                    {totalRecords > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={() => handleChangePage(page - 1)}
                            disabled={page === 0}
                            sx={{
                                color: theme.palette.text.primary,
                                '&:disabled': {
                                    color: theme.palette.text.secondary,
                                    opacity: 0.5,
                                },
                            }}
                        >
                            <MdChevronLeft size={24} />
                        </IconButton>

                        {/* Page numbers */}
                        {[...Array(totalPages)].map((_, index) => {
                            // Show current page and adjacent pages
                            if (
                                index === 0 || // First page
                                index === totalPages - 1 || // Last page
                                (index >= page - 1 && index <= page + 1) // Current and adjacent
                            ) {
                                return (
                                    <Box
                                        key={index}
                                        onClick={() => handleChangePage(index)}
                                        sx={{
                                            minWidth: 40,
                                            height: 40,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            backgroundColor:
                                                page === index
                                                    ? theme.palette.primary.main
                                                    : 'transparent',
                                            color:
                                                page === index
                                                    ? theme.palette.blackColor.black1
                                                    : theme.palette.text.primary,
                                            fontWeight: page === index ? 600 : 400,
                                            // marginLeft: "10px",
                                            '&:hover': {
                                                backgroundColor:
                                                    page === index
                                                        ? theme.palette.primary.dark
                                                        : 'rgba(245, 166, 35, 0.1)',
                                            },
                                        }}
                                    >
                                        {index + 1}
                                    </Box>
                                );
                            }
                            // Show dots for skipped pages
                            if (
                                index === page - 2 ||
                                index === page + 2
                            ) {
                                return (
                                    <Typography
                                        key={index}
                                        variant="body2"
                                        sx={{ color: theme.palette.text.secondary, px: 1 }}
                                    >
                                        ...
                                    </Typography>
                                );
                            }
                            return null;
                        })}

                        <IconButton
                            onClick={() => handleChangePage(page + 1)}
                            disabled={page >= totalPages - 1}
                            sx={{
                                color: theme.palette.text.primary,
                                '&:disabled': {
                                    color: theme.palette.text.secondary,
                                    opacity: 0.5,
                                },
                            }}
                        >
                            <MdChevronRight size={24} />
                        </IconButton>
                    </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ReusableTable;